import React from 'react';
import { DraftMention } from './DraftMention';

// Matches d_ followed by exactly 12 hex chars, word-bounded to avoid false positives.
const DRAFT_ID_RE = /\bd_[a-f0-9]{12}\b/g;

/**
 * Split a plain text string by DRAFT_ID_RE and return an array of React nodes:
 * - plain text fragments become strings
 * - each draft_id token becomes a <DraftMention> pill
 *
 * Call this instead of rendering raw children strings inside p/li/etc. components.
 */
export function injectDraftMentions(text: string, keyPrefix: string): React.ReactNode[] {
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  // Reset lastIndex before use (the regex flag 'g' is stateful on the literal)
  DRAFT_ID_RE.lastIndex = 0;

  // eslint-disable-next-line no-cond-assign
  while ((match = DRAFT_ID_RE.exec(text)) !== null) {
    const [draftId] = match;
    const before = text.slice(lastIndex, match.index);
    if (before) parts.push(before);
    parts.push(<DraftMention key={`${keyPrefix}-${match.index}`} draftId={draftId} />);
    lastIndex = DRAFT_ID_RE.lastIndex;
  }

  const tail = text.slice(lastIndex);
  if (tail) parts.push(tail);

  return parts;
}

/**
 * Walk React children and replace any string child that contains a draft_id
 * pattern with the expanded node array. Non-string children (elements) are
 * passed through unchanged — this prevents us from accidentally descending
 * into <code>/<pre> elements.
 */
export function withDraftMentions(
  children: React.ReactNode,
  keyPrefix: string,
): React.ReactNode {
  return React.Children.map(children, (child, idx) => {
    if (typeof child === 'string') {
      if (!DRAFT_ID_RE.test(child)) return child;
      // Reset lastIndex after the test() call
      DRAFT_ID_RE.lastIndex = 0;
      const nodes = injectDraftMentions(child, `${keyPrefix}-${idx}`);
      // If no expansion happened, keep string form to avoid wrapping in a Fragment
      return nodes.length === 1 && typeof nodes[0] === 'string' ? nodes[0] : <>{nodes}</>;
    }
    return child;
  }) ?? null;
}
